import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { opsApi } from "../lib/api";
import { eventWords, activityPath } from "../lib/activity";

/** The event log, read back as a story. Every row jumps to the record it is about. */
export default function ActivityFeed({ projectId }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    opsApi.events(projectId, { limit: 40 }).then((d) => setEvents(d.events || []));
  }, [projectId]);

  const go = (e) => window.dispatchEvent(new CustomEvent("abs:navigate", {
    detail: { path: activityPath(projectId, { event_type: e.event_type, related_record: relatedOf(e) }) } }));

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="ops-activity-feed">
      <div className="mb-3 flex items-center gap-2">
        <Activity className="h-4 w-4 text-slate-400" />
        <h2 className="text-base font-semibold text-slate-900">Activity</h2>
      </div>
      {events.length === 0 ? (
        <p className="text-xs text-slate-400">Nothing has happened on this job yet.</p>
      ) : (
        <ol className="max-h-[25rem] space-y-1 overflow-y-auto pr-1" data-testid="ops-activity-list">
          {events.map((e) => (
            <li key={e.id} role="link" tabIndex={0} data-kb-row onClick={() => go(e)}
                onKeyDown={(ev) => { if (ev.key === "Enter") go(e); }}
                className="flex cursor-pointer gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                data-testid={`ops-event-${e.event_type}`} title="Open this record">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{eventWords(e.event_type)}</p>
                <p className="text-xs text-slate-500">
                  {e.actor} · {new Date(e.timestamp).toLocaleString()}
                  {e.source !== "ui" ? ` · ${e.source}` : ""}
                </p>
                {e.note && <p className="text-xs italic text-slate-500">{e.note}</p>}
                {e.payload?.after?.note && (
                  <p className="text-xs italic text-slate-600">“{e.payload.after.note}”</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function relatedOf(e) {
  const rel = e.related_record || {};
  const after = e.payload?.after || e.after || {};
  if (rel.id) return rel;
  if (after.intake_id) return { kind: "intake", id: after.intake_id };
  if (after.co_id) return { kind: "change_order", id: after.co_id };
  return {};
}
